import { createContext, useContext } from 'react'

import { GridPinnedColumns, GridSortItem } from '@mui/x-data-grid-pro'

import { PageSizeOption, UIFilters } from 'routes/highschools/types'

import { standardViews } from './defaultViews'
import { CreateHSExploreView, HSExploreView, HSExploreViews } from './types'

type ViewsContextType = {
  activeView: HSExploreView
  isLoading?: boolean
  views: HSExploreViews
  currentPage: number
  pageSize: PageSizeOption
  changeActiveView: (view: HSExploreView) => void
  createView: (view: CreateHSExploreView) => void
  updateView: (view: HSExploreView) => void
  deleteView: (viewId: string) => void
  updatePinnedColumns: (pinnedColumns: GridPinnedColumns) => void
  updateColumnVisibility: (visibilityModel: { [key: string]: boolean }) => void
  updateColumnOrder: (orderModel: { [key: string]: number }) => void
  updateColumnWidth: (widthModel: { [key: string]: number }) => void
  updateDefaultView: (id: string) => void
  updateColumnSort: (sortModel: GridSortItem) => void
  updatePageSize: (pageSize: PageSizeOption) => void
  updateCurrentPage: (page: number) => void
  updateColumnFilters: (filters: UIFilters) => void
  updateColumnConfig: (field: string, config: Record<string, unknown>) => void
}

const defaultContextValue: ViewsContextType = {
  activeView: standardViews.views[0],
  isLoading: true,
  currentPage: 0,
  pageSize: 25,
  views: {
    views: standardViews.views,
    defaultViewId: standardViews.defaultViewId,
  },
  changeActiveView: () => {},
  createView: () => {},
  updateView: () => {},
  deleteView: () => {},
  updateColumnVisibility: () => {},
  updatePinnedColumns: () => {},
  updateColumnOrder: () => {},
  updateColumnWidth: () => {},
  updateDefaultView: () => {},
  updateColumnSort: () => {},
  updatePageSize: () => {},
  updateCurrentPage: () => {},
  updateColumnFilters: () => {},
  updateColumnConfig: () => {},
}

export const ViewsContext = createContext<ViewsContextType>(defaultContextValue)
export const normalizeOrderModel = (orderModel: { [key: string]: number }): { [key: string]: number } => {
  const defaultKeys = Object.keys(standardViews.views[0]?.orderModel ?? {})
  const allKeys = [...new Set([...Object.keys(orderModel), ...defaultKeys])]
  const existingValues = Object.entries(orderModel)
    .filter(([key]) => defaultKeys.includes(key))
    .map((entry) => entry[1])
    .sort((a, b) => a - b)

  const valueMap = new Map(existingValues.map((value, index) => [value, index + 1]))
  const newColumnStartIndex = existingValues.length + 1

  return allKeys.reduce(
    (acc, key) => {
      const value = orderModel[key]
      if (defaultKeys.includes(key)) {
        acc[key] = valueMap.get(value) ?? 1
      } else {
        acc[key] = newColumnStartIndex + (value ?? 0)
      }
      return acc
    },
    {} as { [key: string]: number },
  )
}

export function useViews() {
  const context = useContext(ViewsContext)
  if (context === undefined) {
    throw new Error('useViews must be used within a ViewsProvider')
  }
  return context
}
